/**
 * The founder's pilot gate, declared once for both sides (docs/pilot-access.html).
 *
 * `/pilot` is not a product mount and carries no route descriptors: it is the
 * three calls that open, read and close one session, and the only thing the four
 * frontends ever do with it is ask "am I in a pilot session, and if so what does
 * the demo world look like". That question has to be asked of the server because
 * the cookie is HttpOnly - no script can read it - and it has to be asked the
 * same way by every portal, which is why the probe lives here rather than being
 * re-implemented three times with three slightly different notions of failure.
 *
 * The schema is the contract: the worker's `pilotContext()` is typed as
 * `PilotContext`, so a field it stops returning is a typecheck failure in the
 * worker rather than an `undefined` on the launcher at review time.
 */

import { z } from 'zod';
import { BakeryStorefrontMode, StorefrontMode } from './enums';

export const PILOT_COOKIE_NAME = 'cib_pilot';
/**
 * The header a pilot session names its demo tenant or counter on (BAK-028):
 * an opaque signed scope, never an organization id. The worker's gate reads it,
 * its CORS list allows it, the partner portal sends it, and the demo health
 * sweep names a counter with it.
 */
export const PILOT_ORG_HEADER = 'x-pilot-org';
export const PILOT_LOGIN_PATH = '/pilot/login';
export const PILOT_CONTEXT_PATH = '/pilot/context';
export const PILOT_ORGANIZATIONS_PATH = '/pilot/organizations';
export const PILOT_APARTMENTS_PATH = '/pilot/apartments';
export const PILOT_LOGOUT_PATH = '/pilot/logout';

const guestEntrySchema = z.object({
  /** The printed `/q/<token>` entry - the only way into the guest app (TST-008). */
  url: z.string(),
  /**
   * The APARTMENT whose printed code this is - what the launcher names the entry
   * by, because that is what the link actually does: it opens the storefront a
   * guest standing in that apartment gets. It used to be named by the bakery
   * below, which describes the shop the storefront happens to lead to rather
   * than the place the scan happened, and two entries differing only by bakery
   * name said nothing about which apartment either of them was.
   */
  property: z.string(),
  /** The shop that apartment's mode resolves to. Kept: the mode B chooser is
   *  asserted against it, and it is the fact that distinguishes the two modes. */
  bakery: z.string(),
});

export const pilotContextSchema = z.object({
  platformDomain: z.string(),
  environment: z.string(),
  portals: z.object({
    guest: z.string(),
    owner: z.string(),
    internal: z.string(),
  }),
  guestEntries: z.object({
    /** Curated storefront. */
    modeA: guestEntrySchema,
    /** Branded (single-bakery) storefront. */
    modeB: guestEntrySchema,
  }),
  partner: z.object({ name: z.string(), orgId: z.string() }),
  /**
   * The bakery organization a pilot partner session may switch to (BAK-028): the
   * partner portal serves bakery organizations too, and the pilot chooses which
   * demo tenant a `/c` session is scoped to with the `x-pilot-org: bakery`
   * request header.
   */
  partnerBakery: z.object({ name: z.string(), orgId: z.string() }),
  /** False when the QR links were derived rather than read from a seeded world. */
  seeded: z.boolean(),
  /**
   * Does THIS cookie open `/internal`? Only an agent's does; a person's opens
   * the demo tenants and leaves Internal to the Google sign-in. Optional on the
   * wire so a portal deployed ahead of its worker reads the absence as "no".
   */
  opensInternal: z.boolean().optional(),
  expiresAt: z.string(),
});

export type PilotContext = z.infer<typeof pilotContextSchema>;

/**
 * One organization a pilot `/c` session may be scoped to (BAK-028).
 *
 * `scope` is the whole point: an opaque value the worker signed, sent back as
 * `x-pilot-org` on every API call. The dashboard never composes it and never
 * sends an organization id of its own - the reachable set is exactly what this
 * list published, which is what stops a typed id from provisioning an
 * organization that was never in the demo world (apps/api-worker/src/pilot.ts).
 */
export const pilotOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['CONCIERGERIE', 'INDEPENDENT_OWNER', 'BAKERY']),
  /**
   * A bakery's own immutable storefront mode (OPS-013); null for a property
   * organization, which has no such fact.
   *
   * On the wire because the selector groups by it: the demo world holds thirty
   * bakeries, and a mode A shop and a mode B house are two different kinds of
   * tenant with two different portals' worth of behaviour behind them. One
   * "Bakeries" group of thirty mixed rows made the two indistinguishable at the
   * moment of choosing, which is the only moment that matters here.
   */
  storefrontMode: BakeryStorefrontMode.nullable(),
  scope: z.string(),
  /**
   * A bakery house's COUNTERS, each with the scope that opens its `/b` board.
   *
   * A counter is one shop's, not a house's: a house with three counters has
   * three boards, and `scripts/check-demo-health.mjs` sweeps `/b` as one of
   * them. So the scope here is a different signed value from the one above -
   * that one names an organization for the owner portal, this one names an
   * organization AND a location for `/b` - and the worker signs both, for the
   * reason `scope` exists at all.
   *
   * Empty for a property organization, which has no counters.
   */
  locations: z.array(z.object({ id: z.string(), name: z.string(), scope: z.string() })),
});

export const pilotOrganizationsSchema = z.object({
  items: z.array(pilotOrganizationSchema),
});

export type PilotOrganization = z.infer<typeof pilotOrganizationSchema>;

/**
 * Every organization this deployment's demo world can show in the owner portal.
 *
 * Empty for every negative answer, for the reasons `probePilotSession` gives:
 * a caller here is filling a selector, and a gate that is off, a lapsed cookie
 * and an unreachable API all mean the same thing to it - there is nothing to
 * offer, so leave the session on whatever it is already scoped to.
 */
export async function fetchPilotOrganizations(
  baseUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<readonly PilotOrganization[]> {
  try {
    const init: Record<string, unknown> = {
      credentials: 'include',
      headers: { accept: 'application/json' },
    };
    const response = await fetchImpl(`${baseUrl}${PILOT_ORGANIZATIONS_PATH}`, init as RequestInit);
    if (!response.ok) return [];
    const parsed = pilotOrganizationsSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.items : [];
  } catch {
    return [];
  }
}

/**
 * One apartment a pilot guest entry may be opened in.
 *
 * An entry is a SCAN: one printed code, on one wall, in one apartment - which is
 * why the unit here is the QR code rather than the apartment. The launcher's two
 * guest sections are the two storefront modes, and `mode` is what decides which
 * section may offer this one: a mode A code opens the curated storefront and a
 * mode B code opens one house's, and offering either in the other section would
 * be a link that opens something other than the section it sits in.
 *
 * The mode is the CODE's own (`qr_codes.storefront_mode`), not its apartment's -
 * the same apartment may carry one of each, and it is the scanned code that
 * decides what the guest sees.
 */
export const pilotApartmentSchema = z.object({
  /** The QR code's id - the identity of this entry, and its `<option>` value. */
  id: z.string(),
  /** The apartment the code is mounted in: what the entry is named by. */
  property: z.string(),
  /** Its owner - a conciergerie or an independent owner - so two apartments
   *  sharing a name are still distinguishable at the moment of choosing. */
  partner: z.string(),
  /** The APARTMENT's menu type, which since 2026-09-20 may also be B2. */
  mode: StorefrontMode,
  /** The printed `/q/<token>` entry, composed by the worker (PRD OPS-018). */
  url: z.string(),
});

export const pilotApartmentsSchema = z.object({
  items: z.array(pilotApartmentSchema),
});

export type PilotApartment = z.infer<typeof pilotApartmentSchema>;

/**
 * Every apartment whose printed code this deployment's demo world can open.
 *
 * Empty for every negative answer, for the reason `fetchPilotOrganizations`
 * gives: the caller is filling a selector, and a gate that is off, a lapsed
 * cookie and an unreachable API all mean the same thing to it - there is nothing
 * to offer, so leave the entries on the two the context already names.
 */
export async function fetchPilotApartments(
  baseUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<readonly PilotApartment[]> {
  try {
    const init: Record<string, unknown> = {
      credentials: 'include',
      headers: { accept: 'application/json' },
    };
    const response = await fetchImpl(`${baseUrl}${PILOT_APARTMENTS_PATH}`, init as RequestInit);
    if (!response.ok) return [];
    const parsed = pilotApartmentsSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.items : [];
  } catch {
    return [];
  }
}

/**
 * The portal subdomains - the ONE list of them; the worker's CORS grant and its
 * `PortalSubdomain` type are read from it.
 *
 * `baker` is not one any more. It served the web counter tablet, retired on
 * 2026-10-08; the host now carries only the pairing page, rendered by the API
 * worker itself, which calls no API from a browser and so needs no CORS grant
 * (apps/api-worker/src/pairing-page.ts).
 *
 * Ordered longest-matching first, and the order is load-bearing here: it is
 * used to recover an instance PREFIX from a host with `endsWith`: `dapp.` is
 * the demo instance's partner dashboard, so its subdomain is the prefix `d`
 * followed by the portal name `app`. Matching the NAME rather than assuming a
 * one-character prefix is what keeps this working for any prefix the
 * deployment chooses. (A reader that only enumerates the portals, like CORS,
 * does not care about the order.)
 */
export const PORTAL_SUBDOMAINS = ['internal', 'order', 'app', 'www'] as const;

/**
 * Where the pilot launcher lives, derived from the portal being served rather
 * than from a configured host (PRD OPS-018).
 *
 * The launcher is a SCREEN inside the operations portal now - `internal.`,
 * `/pilot` - rather than a static page on `www.` (plan website-app-split
 * section 4). Two consequences for this derivation. It keeps the instance
 * prefix, because the launcher is part of a per-instance deployment at last:
 * `dapp.croissantinbed.com` -> `https://dinternal.croissantinbed.com/pilot`, and
 * `app.` -> `internal.`. And on a local run it points at the internal dev server's
 * port rather than the partner one's.
 *
 * Shared by the other staff portals: each one needs to point a founder who
 * opened it without signing in at the one gate, and three copies of this rule
 * would be three chances for one of them to keep pointing at croissantinbed.com
 * after the move to croissantinbed.com.
 */
export function pilotLauncherUrl(location: { protocol: string; hostname: string }): string {
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return `${location.protocol}//${location.hostname}:5176/pilot`;
  }
  const parts = location.hostname.split('.');
  if (parts.length <= 2) return `${location.protocol}//internal.${location.hostname}/pilot`;
  const subdomain = parts[0] ?? '';
  const domain = parts.slice(1).join('.');
  const portal = PORTAL_SUBDOMAINS.find((name) => subdomain.endsWith(name));
  const prefix = portal === undefined ? '' : subdomain.slice(0, subdomain.length - portal.length);
  return `${location.protocol}//${prefix}internal.${domain}/pilot`;
}

/**
 * Mint or slide the `cib_pilot` cookie from a Clerk session.
 *
 * The credentials are the caller's own - internal-admin passes the very headers it
 * puts on every other request - and the cookie comes back on the response,
 * which is why `credentials: 'include'` matters as much here as on the probe.
 * True when a session now exists; false for every refusal, which the caller
 * reports as one thing because the API deliberately reports it as one thing.
 */
export async function startPilotSession(
  baseUrl: string,
  authHeaders: Record<string, string>,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const init: Record<string, unknown> = {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json', ...authHeaders },
    };
    const response = await fetchImpl(`${baseUrl}${PILOT_LOGIN_PATH}`, init as RequestInit);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Clear it. One shared cookie means one shared sign-out: this ends the session
 * on all four portals at once, which is the honest behaviour for a credential
 * that opened all four.
 */
export async function endPilotSession(
  baseUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    const init: Record<string, unknown> = { method: 'POST', credentials: 'include' };
    await fetchImpl(`${baseUrl}${PILOT_LOGOUT_PATH}`, init as RequestInit);
  } catch {
    // Best effort: the caller's next probe is what decides whether it worked.
  }
}

/**
 * Is this browser in a pilot session, and what world does it see?
 *
 * `null` for every negative answer - no cookie or an expired one (401), the API
 * unreachable, a body that does not match the schema. A portal
 * calling this is deciding whether to offer a sign-in screen, and every one of
 * those cases has the same answer: yes, offer one.
 *
 * `credentials: 'include'` is the whole point. The portals are on `baker.` and
 * `internal.` while the API is on `api.`, so every one of these calls is
 * cross-origin, and a cross-origin fetch sends no cookies at all unless it says
 * so - the request would arrive anonymous and the probe would answer "not in a
 * pilot session" on a browser that is.
 */
export async function probePilotSession(
  baseUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PilotContext | null> {
  try {
    // Widened for the same reason as in `client.ts`: a Worker's `RequestInit`
    // has no `credentials` field, and this module is compiled by the worker too.
    const init: Record<string, unknown> = {
      credentials: 'include',
      headers: { accept: 'application/json' },
    };
    const response = await fetchImpl(`${baseUrl}${PILOT_CONTEXT_PATH}`, init as RequestInit);
    if (!response.ok) return null;
    const parsed = pilotContextSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
